import React, { useState, useEffect } from "react";
import userServices from "../components/Services/UserService";
import { showErrorToast, showSuccessToast } from "../components/Constant/Notification";
import { validateInput } from "../utils/regexValidators";
import { useNavigate } from "react-router-dom";
import { getBasePathByUserRole } from "../utils/roleRoutes";
import teamService from "../components/Services/TeamService";


const useUser = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedRole, setSelectedRole] = useState(null);
    const [visibleForm, setVisibleForm] = useState(false);
    const [userList, setUserList] = useState([]);
    const [userCount, setUserCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [pageToken, setPageToken] = useState(null);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [prevPageToken, setPrevPageToken] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pageTokens, setPageTokens] = useState([null]);
    const [cache] = useState(() => new Map());

    const navigate = useNavigate();

    const [roleCount, setRoleCount] = useState({})

    const [dealCount, setDealCount] = useState(0);
    const [leadCount, setLeadCount] = useState(0);
    const [status, setStatus] = useState(true);
    const [infoShow, setInfoShow] = useState(false);
    const [users, SetUsers] = useState([]);

    const [userForm, setUserForm] = useState({
        userId: "",
        name: "",
        email: "",
        empId: "",
        phone: "",
        role: "",
        team: "",
        status: true,
        isIvr : false,
    })

    const getCacheKey = (page, search, role) => `${page}_${search}_${role || "ALL"}`;

    const getUserList = async ({
        search = search,
        page = 1,
        direction = "",
        pageToken: customToken = null,
        role = null,
    } = {}) => {
        const cacheKey = getCacheKey(page, search, role);
        const tokenForPage = customToken ?? pageTokens[page - 1] ?? null;

        if (cache.has(cacheKey)) {
            const cachedData = cache.get(cacheKey);
            setUserList(cachedData.users);
            setUserCount(cachedData.maxCount);
            setHasMore(cachedData.hasMore);
            setNextPageToken(cachedData.nextPageToken);
            setPrevPageToken(cachedData.prevPageToken);
            setCurrentPage(page);
            setPageToken(tokenForPage);
            console.log("Data loaded from cache for page:", page, "cacheKey:", cacheKey);
            return;
        }

        try {
            setLoading(true);

            const body = {
                limit: pageSize,
                searchKey: search,
                role: role ? role.toUpperCase() : null,
                next: direction === "next" ? tokenForPage : null,
                prev: direction === "prev" ? tokenForPage : null,
                direction,
            };

            const response = await userServices.getUserData(body);
            const users = response.data.users || [];
            const maxCount = response.data.maxCount || 0;
            const nextToken = response.data.next || null;
            const prevToken = response.data.prev || null;

            const calculatedHasMore = page * pageSize < maxCount;

            const cacheData = {
                users,
                maxCount,
                hasMore: calculatedHasMore,
                nextPageToken: nextToken,
                prevPageToken: prevToken,
            };

            cache.set(cacheKey, cacheData);
            console.log("Data cached for page:", page, "cacheKey:", cacheKey);

            if (direction === "next" && nextToken && calculatedHasMore) {
                setPageTokens((prev) => {
                    const newTokens = [...prev];
                    if (newTokens.length === page) {
                        newTokens.push(nextToken);
                    }
                    return newTokens;
                });
            } else if (direction === "previous" && prevToken && page > 1) {
                setPageTokens((prev) => {
                    const newTokens = [...prev];
                    if (newTokens.length >= page && newTokens[page - 1] !== prevToken) {
                        newTokens[page - 1] = prevToken;
                    }
                    return newTokens;
                });
            }

            setUserList(users);
            setUserCount(maxCount);
            setHasMore(calculatedHasMore);
            setNextPageToken(nextToken);
            setPrevPageToken(prevToken);
            setPageToken(direction === "next" ? nextToken : prevToken);
            setCurrentPage(page);
        } catch (error) {
            console.error("Error fetching user list:", error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        cache.clear();
        setCurrentPage(1);
        setPageToken(null);
        setPageTokens([null]);
        getUserList({ search: searchTerm, page: 1, role: selectedRole });
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handlePageChange = (token, direction) => {
        console.log("Page change requested:", { token, direction, currentPage });

        if (direction === "next" && hasMore) {
            const newPage = currentPage + 1;
            console.log("Moving to next page:", newPage, "with token:", nextPageToken);
            getUserList({
                search: searchTerm,
                pageToken: nextPageToken,
                page: newPage,
                direction: "next",
                role: selectedRole,
            });
        } else if (direction === "previous" && currentPage > 1) {
            const newPage = currentPage - 1;
            const prevToken = pageTokens[newPage - 1] || prevPageToken;
            console.log("Moving to previous page:", newPage, "with token:", prevToken);
            getUserList({
                search: searchTerm,
                pageToken: prevToken,
                page: newPage,
                direction: "prev",
                role: selectedRole,
            });
        }
    };

    const handlePageSizeChange = (newPageSize) => {
        console.log("Page size changed to:", newPageSize);
        setPageSize(newPageSize);
    };

    const handleRoleChange = (role) => {
        setSelectedRole(role);
    };

    useEffect(() => {
        cache.clear();
        setCurrentPage(1);
        setPageToken(null);
        setPageTokens([null]);
        getUserList({ search: searchTerm, page: 1, role: selectedRole });
    }, [pageSize, selectedRole]);


    const handleUserDetails = async (userData) => {

         if(userData){
            console.log("Handling user details for:", userData);
        
        }
        setUserForm({
            userId: userData.uid || "",
            name: userData.name || "",
            email: userData.emailId || "",
            empId: userData.employeeId || "",
            phone: userData.phoneNumber || "",
            role: userData.role || "",
            team: userData.teamName || [],
            status: userData.status !== undefined ? userData.status : true,
            isIvr : userData.isIvr || false,
        });

    }

    const fetchUserCount = async () => {
        try {
            const response = await userServices.getUserCount();
            console.log("User count response:", response);
            if (response.status === 200) {
                setRoleCount(response.data || 0);
            } else {
                console.error("Failed to fetch user count:", response.data);
            }
        } catch (error) {
            console.error("Error fetching user count:", error);
        }
    }
    useEffect(() => {
        fetchUserCount();
    }, []);

    const handleUserCreate = async (userData) => {

        console.log("Creating user with data:", userData);
        if (!userData.name || userData.name.trim() === "") {
            showErrorToast("Please enter a valid name.");
            return;
        }
        if (!userData.employeeId || userData.employeeId?.trim() === "") {
            showErrorToast("Please enter a valid employee ID.");
            return;
        }
        if (!userData.phoneNumber || !validateInput("phone", userData.phoneNumber)) {
            showErrorToast("Please enter a valid phone number.");
            return;
        }

        if (!userData.role || userData.role.trim() === "") {
            showErrorToast("Please select a valid role.");
            return;
        }
        const showTeamSelect = ["ADMIN", "CMO", "LEAD UPLOADER"].includes(
            userData.role.toUpperCase()
        );
        if (!showTeamSelect) {
            if (!userData.team || userData.team.length === 0) {
                showErrorToast("Please select a valid team.");
                return;
            }
        }

        if (!validateInput("email", userData.emailId)) {
            showErrorToast("Please enter a valid email address.");
            return;
        }


setLoading(true);
        try {

            const response = await userServices.createUser(userData);
            console.log("User creation response:", response);

            if (response.status === 200) {

                showSuccessToast(response.data.message || "User created successfully");

                setVisibleForm(false);
                setUserForm({
                    name: "",
                    email: "",
                    empId: "",
                    phone: "",
                    role: "",
                    team: "",
                    status: true,
                    isIvr : false,

                });
                cache.clear();
                setCurrentPage(1);
                setPageToken(null);
                setPageTokens([null]);
                fetchUserCount()

                getUserList({ search: searchTerm, page: 1, role: selectedRole });
            } else {
                console.error("Failed to create user:", response.data);
            }
        } catch (error) {
            console.error("Error creating user:", error);
        }finally{
            setLoading(false);
        }
    }

    const handleUserUpdate = async (userId, userData) => {
        console.log("Updating user with ID:", userId, "and data:", userData);
        if (!userId || !userData) {
            showErrorToast("Invalid user ID or data.");
            return;
        }
        if (!userData.name || userData.name.trim() === "") {
            showErrorToast("Please enter a valid name.");
            return;
        }
        if (!userData.employeeId || userData.employeeId?.trim() === "") {
            showErrorToast("Please enter a valid employee ID.");
            return;
        }
        if (!userData.phoneNumber || !validateInput("phone", userData.phoneNumber)) {
            showErrorToast("Please enter a valid phone number.");
            return;
        }
        

        try {
            const response = await userServices.updateUser(userId, userData);
            console.log("User update response:", response);

            if (response.status === 200) {
                showSuccessToast(response.data.message || "User updated successfully");
                setVisibleForm(false);
                setUserForm({
                    name: "",
                    email: "",
                    empId: "",
                    phone: "",
                    role: "",
                    team: "",
                    status: true,
                });
                cache.clear();
                setCurrentPage(1);
                setPageToken(null);
                setPageTokens([null]);
                getUserList({ search: searchTerm, page: 1, role: selectedRole });
            } else {
                console.error("Failed to update user:", response.data);
            }
        } catch (error) {
            console.error("Error updating user:", error);
        }
    }


    const handleToggleClick = async (userId) => {
        setLoading(true)
        try {
            const body = {
                uid: userId,
            };
            const response = await userServices.checkUserDeactivation(body);
            setStatus(!status);
            setInfoShow(true);

            setVisibleForm(false);
            const data = response?.data;
            console.log(response);
            if (response.status === 200) {
                console.log(data);
                showSuccessToast(response?.data);
                setDealCount(data.dealCount);
                setLeadCount(data?.leadCount);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };


    const handleDeactiveUser = async (userId) => {
        
        try {
            const body = {
                uid: userId,
                dealCount: dealCount,
                leadCount: leadCount,
                status: false,
            };
            const response = await userServices.userDeactivation(body);

            console.log(response);
            if (response.status === 200) {
                showSuccessToast(response?.data);
                cache.clear();
                setInfoShow(false);
                setCurrentPage(1);
                setPageToken(null);
                setPageTokens([null]);
                getUserList({ search: searchTerm, page: 1, role: selectedRole });
                setUserForm({
                    name: "",
                    email: "",
                    empId: "",
                    phone: "",
                    role: "",
                    team: "",
                    status: true,
                });
            }
        } catch (error) {
            console.log(error);
        }
    };

    const path = getBasePathByUserRole()

    const handleReassignLead = (userId, incomingTeamId) => {
        const finalTeamId =
          Array.isArray(incomingTeamId) && incomingTeamId.length > 0
            ? incomingTeamId[0]
            : ''; 
      
        navigate(`${path}/lead-reasign-lead`, {
          state: {
            userId: userId,
            teamId: finalTeamId,
            filterOpen: true,
          },
        });
      };
      
    const userData = async () => {
        try {
            const userResponse = await teamService.getUserName();
            SetUsers(userResponse.data.users);
            console.log(userResponse)
        } catch (error) {
            console.log("Error");
        }
    }
    useEffect(() => {
      if(leadCount || dealCount)
      {
        userData();
      }
            
    }, [leadCount])

    const handleDealReassign = async (userId) => {



        navigate(`${path}/pipline-user-deal`, {
            state: {
                userId: userId,
                userList: users,
            }
        });
    }

    return {
        searchTerm,
        setSearchTerm,
        selectedRole,
        handleRoleChange,
        userList,
        userCount,
        pageSize,
        pageToken,
        nextPageToken,
        prevPageToken,
        currentPage,
        hasMore,
        loading,
        handleSearchSubmit,
        handleSearchChange,
        handlePageChange,
        handlePageSizeChange,
        userForm,
        setUserForm,
        handleUserCreate,
        userForm,
        setUserForm,
        visibleForm,
        setVisibleForm,
        handleUserDetails,
        handleUserUpdate,
        handleToggleClick,
        roleCount,
        dealCount,
        leadCount,
        status,
        infoShow,
        setInfoShow,
        handleReassignLead,
        handleDealReassign,
        handleDeactiveUser,
        users,



    };
};

export default useUser;